import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Activity, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import "@/i18n/fase6";

const fmtUptime = (s) => {
  if (!s) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
};

export default function Monitoring() {
  const { t } = useTranslation();
  const { data: summary } = useSWR("/admin/monitoring/summary", fetcher, { refreshInterval: 15000 });
  const { data: integrations } = useSWR("/admin/monitoring/integrations", fetcher, { refreshInterval: 30000 });
  const { data: rate } = useSWR("/admin/monitoring/rate-limits", fetcher, { refreshInterval: 30000 });
  const { data: tasks } = useSWR("/admin/monitoring/tasks", fetcher, { refreshInterval: 30000 });
  const { data: audit } = useSWR("/admin/monitoring/audit-events", fetcher, { refreshInterval: 60000 });
  const { data: hourly } = useSWR("/admin/monitoring/hourly", fetcher, { refreshInterval: 60000 });

  const integCount = integrations
    ? [integrations.tristrash_ops?.healthy, integrations.emergent_llm?.healthy].filter(Boolean).length
    : "—";

  return (
    <div data-testid="monitoring-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone">{t("fase6.sidebar_monitoring")}</div>
        <h1 className="font-serif text-3xl text-salnes-ink">{t("fase6.mon_title")}</h1>
        <div className="text-sm text-salnes-stone mt-1">{t("fase6.mon_subtitle")}</div>
      </div>

      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="mon-kpis">
        <Kpi label={t("fase6.mon_uptime")} value={fmtUptime(summary?.platform_uptime_seconds)} />
        <Kpi label={t("fase6.mon_requests_1h")} value={summary?.requests_last_1h ?? "—"} />
        <Kpi label={t("fase6.mon_error_rate")} value={summary ? `${summary.error_rate_1h_pct}%` : "—"} />
        <Kpi label={t("fase6.mon_avg_latency")} value={summary?.avg_latency_ms ?? "—"} />
        <Kpi label={t("fase6.mon_open_hits")} value={rate?.open_api_hits_1h ?? "—"} />
        <Kpi label={t("fase6.mon_integrations_ok")} value={integCount} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4" data-testid="mon-charts-row">
        <div className="rounded-2xl border hairline bg-white p-5" data-testid="mon-line-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">
            {t("fase6.mon_chart_line_title")}
          </div>
          <div style={{ width: "100%", height: 220 }} data-testid="monitoring-line-chart">
            <ResponsiveContainer>
              <LineChart data={hourly?.hourly || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" fontSize={10} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} stroke="#6B7280" />
                <YAxis fontSize={10} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} stroke="#6B7280" allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E5E7EB" }} />
                <Line
                  type="monotone"
                  dataKey="requests"
                  stroke="#0B4C7A"
                  strokeWidth={2}
                  dot={{ r: 2, fill: "#0B4C7A" }}
                  activeDot={{ r: 4 }}
                  name={t("fase6.mon_chart_line_series")}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border hairline bg-white p-5" data-testid="mon-bar-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">
            {t("fase6.mon_chart_bar_title")}
          </div>
          <div style={{ width: "100%", height: 220 }} data-testid="monitoring-bar-chart">
            <ResponsiveContainer>
              <BarChart
                data={(hourly?.top_datasets || []).map((d) => ({ ...d, dataset: (d.dataset || "").slice(0, 22) }))}
                layout="vertical"
                margin={{ top: 8, right: 12, left: 40, bottom: 0 }}
              >
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" fontSize={10} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} stroke="#6B7280" allowDecimals={false} />
                <YAxis type="category" dataKey="dataset" fontSize={10} width={110} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} stroke="#6B7280" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E5E7EB" }} />
                <Bar dataKey="requests" fill="#10B981" radius={[0, 4, 4, 0]} name={t("fase6.mon_chart_bar_series")} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {(!hourly?.top_datasets || hourly.top_datasets.length === 0) && (
            <div className="text-[11px] text-salnes-stone italic mt-2" data-testid="monitoring-bar-empty">
              {t("fase6.mon_chart_bar_empty")}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="rounded-2xl border hairline bg-white p-5" data-testid="mon-integrations">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.mon_integrations_title")}</div>
          <div className="space-y-2">
            {integrations && (
              <>
                <IntegRow name="TrisTrash Ops" ok={integrations.tristrash_ops?.healthy} note={`${integrations.tristrash_ops?.sources?.length || 0} endpoints`} />
                <IntegRow name="ip-api.com" ok={integrations.ip_api_com?.healthy} note={integrations.ip_api_com?.notes} />
                <IntegRow name="Emergent LLM (Gemini Vision)" ok={integrations.emergent_llm?.healthy} note={integrations.emergent_llm?.notes} />
                <IntegRow name={`Selo dixital (${integrations.signature_backend?.active})`} ok={integrations.signature_backend?.healthy} note="Local HMAC · @FIRMA cando dispoñible" />
              </>
            )}
          </div>
        </div>

        <div className="rounded-2xl border hairline bg-white p-5" data-testid="mon-tasks">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.mon_tasks_title")}</div>
          <div className="space-y-2">
            {(tasks?.known_tasks || []).map((tname) => {
              const last = tasks?.last_sync_at?.[tname];
              const err = tasks?.last_error?.[tname];
              return (
                <div key={tname} className="flex items-center justify-between text-sm border-b hairline last:border-0 py-2" data-testid={`mon-task-${tname}`}>
                  <div>
                    <div className="text-salnes-ink font-mono text-[12px]">{tname}</div>
                    <div className="text-[10px] text-salnes-stone">{last ? last.slice(0, 19).replace("T", " ") : "—"}</div>
                  </div>
                  {err ? <Badge className="bg-salnes-coral text-white text-[10px]">degraded</Badge> : <Badge className="bg-salnes-emerald text-white text-[10px]">ok</Badge>}
                </div>
              );
            })}
            {/* siGEUS tasks — Fase 9 */}
            {(tasks?.sigeus_tasks || []).length > 0 && (
              <div className="pt-3 mt-1 border-t hairline">
                <div className="text-[10px] uppercase tracking-[0.16em] text-salnes-atlantic mb-1">siGEUS · Distromel</div>
                {(tasks.sigeus_tasks || []).map((row) => {
                  const status = row.status;
                  const badgeClass =
                    status === "ok" ? "bg-salnes-emerald text-white" :
                    status === "ok_empty" ? "bg-[#F4A261] text-white" :
                    status === "failed" ? "bg-salnes-coral text-white" :
                    "bg-salnes-stone/40 text-white";
                  return (
                    <div
                      key={row.name}
                      className="flex items-center justify-between text-sm border-b hairline last:border-0 py-2"
                      data-testid={`mon-task-${row.name}`}
                    >
                      <div>
                        <div className="text-salnes-ink font-mono text-[12px] flex items-center gap-2">
                          {row.name}
                          <span className="text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-salnes-atlantic/10 text-salnes-atlantic">siGEUS</span>
                        </div>
                        <div className="text-[10px] text-salnes-stone">
                          {row.last_run_at ? row.last_run_at.slice(0, 19).replace("T", " ") : "—"}
                          {row.records != null && <span className="ml-2">· {row.records.toLocaleString()} rec.</span>}
                        </div>
                      </div>
                      <Badge className={`${badgeClass} text-[10px]`}>{status || "—"}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border hairline bg-white p-5" data-testid="mon-rate">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.mon_rate_title")}</div>
          <div className="text-xs text-salnes-stone mb-2">Open API 1h: <b className="text-salnes-ink">{rate?.open_api_hits_1h ?? 0}</b></div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">
              <tr><th className="text-left py-2">Key</th><th className="text-right py-2">{t("fase6.mon_top_consumer")}</th></tr>
            </thead>
            <tbody>
              {(rate?.api_key_top_consumers_24h || []).map((c) => (
                <tr key={c.key_id} className="border-t hairline">
                  <td className="py-2 font-mono text-[11px]">{c.key_id?.slice(0, 12)}…</td>
                  <td className="py-2 text-right tabular-nums">{c.requests}</td>
                </tr>
              ))}
              {(!rate?.api_key_top_consumers_24h || rate.api_key_top_consumers_24h.length === 0) && (
                <tr><td colSpan={2} className="py-3 text-center text-salnes-stone italic text-xs">—</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="rounded-2xl border hairline bg-white p-5" data-testid="mon-audit">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-3">{t("fase6.mon_audit_title")}</div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {(audit?.actions || []).slice(0, 20).map((a) => (
              <div key={a.action} className="flex items-center justify-between text-[12px] border-b hairline last:border-0 py-1.5">
                <code className="text-[11px] bg-salnes-sand/40 px-2 py-0.5 rounded">{a.action}</code>
                <span className="tabular-nums font-medium text-salnes-ink">{a.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value }) => (
  <div className="rounded-2xl border hairline bg-white p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">{label}</div>
    <div className="font-serif text-2xl text-salnes-atlantic tabular-nums mt-1">{value}</div>
  </div>
);

const IntegRow = ({ name, ok, note }) => {
  const Icon = ok ? CheckCircle2 : ok === false ? XCircle : AlertCircle;
  const color = ok ? "text-salnes-emerald" : ok === false ? "text-salnes-coral" : "text-salnes-stone";
  return (
    <div className="flex items-center gap-3 py-2 border-b hairline last:border-0">
      <Icon size={16} className={color} />
      <div className="flex-1">
        <div className="text-sm text-salnes-ink font-medium">{name}</div>
        {note && <div className="text-[10px] text-salnes-stone">{note}</div>}
      </div>
    </div>
  );
};
